import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  FlatList,
  Platform,
  Dimensions,
} from "react-native";
import lightStyles, {
  createHomeScreenOverlayContainerStyle,
  createHomeScreenThemeStyles,
} from "../HomeScreen.style";
import { FontFamily } from "../../../constants/fonts";
import { MetricsItem } from "../types";
import LHOverlay from "../../../components/LHOverlay";

// Import from constants files directly to ensure they're available
import { BREAKPOINTS } from "../../../constants/common";
import { SCORE_THRESHOLDS, COLORS } from "../../../constants/themes";
import { METRIC_CATEGORIES } from "../../../constants/metrics";
import { useTheme } from "../../../theme";

interface MetricsOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  metricsData: MetricsItem[];
}

// Dark-mode equivalents of COLORS.SUCCESS / COLORS.ERROR. These are exempt from
// the global "all text is white" rule (see KEEP_TEXT_LOOKUP in globalDarkModeWeb.ts)
// when the element carries dataSet={{ lhKeepColor: "1" }}.
const DARK_SCORE_SUCCESS = "#4ADE80";
const DARK_SCORE_ERROR = "#FF6B6B";

const getColorForScore = (
  value: string | number,
  isDark = false
): string | undefined => {
  let numericValue: number;
  if (typeof value === "string") {
    numericValue = parseFloat(value.replace("%", ""));
  } else {
    numericValue = value as number;
  }

  if (isNaN(numericValue as number)) {
    return undefined;
  }

  if ((numericValue as number) >= SCORE_THRESHOLDS.HIGH) {
    return isDark ? DARK_SCORE_SUCCESS : COLORS.SUCCESS;
  }

  return isDark ? DARK_SCORE_ERROR : COLORS.ERROR;
};

const getMetricKey = (metricCategory: string): keyof MetricsItem => {
  switch (metricCategory) {
    case "Pulse check":
      return "pulseCheck";
    case "Observation":
      return "observation";
    case "Shift Handover":
      return "shift";
    case "Leadership Walk":
      return "leadershipWalk";
    case "SPARC Walk":
      return "sparcWalk";
    default:
      return "pulseCheck";
  }
};

const MetricsOverlay: React.FC<MetricsOverlayProps> = ({
  visible,
  onDismiss,
  metricsData,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors), [colors]);
  const overlayContainerStyle = useMemo(
    () => createHomeScreenOverlayContainerStyle(colors),
    [colors]
  );
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const metricCategories = useMemo(() => METRIC_CATEGORIES, []);

  const localStyles = useMemo(
    () => ({
      boldText: {},
      defaultText: {},
      headerText: {},
      noDataContainer: { padding: 20, alignItems: "center" as const },
    }),
    []
  );

  useEffect(() => {
    const updateLayout = () => {
      const windowWidth = Dimensions.get("window").width;
      setIsMobile(Platform.OS !== "web" || windowWidth < BREAKPOINTS.MOBILE);
    };

    updateLayout();
    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateLayout
    );
    return () => dimensionsListener.remove();
  }, []);

  const renderCategoryRow = useCallback(
    (category: string, idx: number) => {
      const metricKey = getMetricKey(category);
      return (
        <View key={idx} style={[lightStyles.metricRow, themeStyles.metricRow]}>
          <Text style={[lightStyles.metricCell, themeStyles.metricCell, lightStyles.metricBoldText, themeStyles.primaryText]}>{category}</Text>
          {metricsData.map((locationItem, i) => {
            const dataValue = locationItem[metricKey];
            const valueStr = String(dataValue || "");
            const numericValue = parseFloat(valueStr.replace("%", ""));
            const isNumeric = !isNaN(numericValue);
            const textColor = isNumeric ? getColorForScore(numericValue, isDark) : undefined;
            return (
              <Text
                key={i}
                style={[
                  lightStyles.metricCell,
                  themeStyles.metricCell,
                  lightStyles.metricDefaultText,
                  textColor ? { color: textColor } : undefined,
                ]}
                {...(isDark && textColor
                  ? ({ dataSet: { lhKeepColor: "1" } } as any)
                  : {})}
              >
                {valueStr}
              </Text>
            );
          })}
        </View>
      );
    },
    [metricsData, localStyles, isDark]
  );

  const renderMobileMetricRow = useCallback(
    ({ index }: { index: number }) => {
      const metricCategory = metricCategories[index];
      const metricKey = getMetricKey(metricCategory);
      return (
        <View style={[lightStyles.metricRow, themeStyles.metricRow]}>
          <Text
            style={[
              lightStyles.metricCell,
              themeStyles.metricCell,
              lightStyles.metricBoldText,
              themeStyles.primaryText,
            ]}
          >
            {metricCategory}
          </Text>
          {metricsData.map((locationItem, i) => {
            const dataValue = locationItem[metricKey];
            const valueStr = String(dataValue || "");
            const numericValue = parseFloat(valueStr.replace("%", ""));
            const isNumeric = !isNaN(numericValue);
            const textColor = isNumeric
              ? getColorForScore(numericValue, isDark)
              : undefined;
            return (
              <Text
                key={i}
                style={[
                  lightStyles.metricCell,
                  themeStyles.metricCell,
                  lightStyles.metricDefaultText,
                  textColor ? { color: textColor } : undefined,
                ]}
                {...(isDark && textColor
                  ? ({ dataSet: { lhKeepColor: "1" } } as any)
                  : {})}
              >
                {valueStr}
              </Text>
            );
          })}
        </View>
      );
    },
    [metricCategories, metricsData, localStyles, isDark]
  );

  const MobileLayout = useMemo(
    () => (
      <>
        <View style={[lightStyles.metricRow, themeStyles.metricRow]}>
          <Text
            style={[
              lightStyles.metricCell,
              lightStyles.metricHeaderCell,
              lightStyles.metricHeaderText,
              themeStyles.metricHeaderCell,
            ]}
          />
          {metricsData.map((item, index) => (
            <Text
              key={index}
              style={[
                lightStyles.metricCell,
                lightStyles.metricHeaderCell,
                lightStyles.metricHeaderText,
                themeStyles.metricHeaderCell,
              ]}
            >
              {item.region}
            </Text>
          ))}
        </View>
        <FlatList
          data={metricCategories}
          renderItem={renderMobileMetricRow}
          keyExtractor={(_, index) => index.toString()}
          scrollEnabled={false}
        />
      </>
    ),
    [metricsData, metricCategories, renderMobileMetricRow, localStyles]
  );

  const WebLayout = useMemo(
    () => (
      <>
        <View style={[lightStyles.metricRow, themeStyles.metricRow]}>
          <Text style={[lightStyles.metricCell, lightStyles.metricHeaderCell, themeStyles.metricHeaderCell, localStyles.headerText]} />
          {metricsData.map((item, index) => (
            <Text key={index} style={[lightStyles.metricCell, lightStyles.metricHeaderCell, themeStyles.metricHeaderCell, localStyles.headerText]}>
              {item.region}
            </Text>
          ))}
        </View>
        {metricCategories.map((category, index) => renderCategoryRow(category, index))}
      </>
    ),
    [metricsData, metricCategories, renderCategoryRow, localStyles]
  );

  const hasData =
    metricsData &&
    metricsData.length > 0 &&
    metricsData.some((item) =>
      Object.values(item).some(
        (value) =>
          value &&
          value !== "0%" &&
          (typeof value === "string" && value.includes("%")
            ? value !== "0%"
            : true)
      )
    );

  return (
    <LHOverlay
      visible={visible}
      onDismiss={onDismiss}
      title={"Lighthouse Metric Scores"}
      containerStyle={overlayContainerStyle}
      hideFooter={true}
      headerPaddingHorizontal={24}
      size={"large"}
    >
      <ScrollView
        style={lightStyles.metricsScrollView}
        contentContainerStyle={lightStyles.metricsScrollViewContent}
        nestedScrollEnabled={true}
        showsVerticalScrollIndicator={true}
      >
        <View style={lightStyles.metricsContainer}>
          {hasData ? (
            isMobile ? (
              MobileLayout
            ) : (
              WebLayout
            )
          ) : (
            <View style={localStyles.noDataContainer}>
              <Text style={[lightStyles.overlayNoDataTitle, themeStyles.overlayNoDataTitle]}>No metrics data available for this location</Text>
                <Text style={[lightStyles.overlayNoDataBody, themeStyles.overlayNoDataBody]}>Data could not be loaded at this time. Please try again later.</Text>
            </View>
          )}
        </View>
      </ScrollView>
      {/* Close button removed to match other overlays */}
    </LHOverlay>
  );
};

export default MetricsOverlay;
